import type { DataItemId } from '@pn/core/domain/data';
import type { WorkspaceItem } from '@pn/core/domain/workspace';
import { getLength } from '@pn/services/utils/geojson/spatial'; // cannot import from index.ts because of circular dependency
import { murmurhash3 } from '@pn/services/utils/hash';
import { isNil, isString } from 'lodash-es';

export enum MapMode {
  Standard = 'standard',
  Light = 'light',
  Dark = 'dark',
  Satellite = 'satellite',
}

export function isMapMode(value: unknown): value is MapMode {
  return Object.values(MapMode).includes(value as MapMode);
}

export type MapFeature = {
  layerId: string;
  itemId: WorkspaceItem['id'];
  dataType: string;
  internalId: DataItemId;
  geometry: GeoJSON.Geometry;
};

export function getMapFeatureIds(features: MapFeature[]): DataItemId[] {
  const seen = new Set<DataItemId>();
  const result: DataItemId[] = [];

  for (const feature of features) {
    if (!seen.has(feature.internalId)) {
      seen.add(feature.internalId);
      result.push(feature.internalId);
    }
  }

  return result;
}

export function hashFeatureId(featureId: string | number): number {
  return isString(featureId) ? murmurhash3(featureId) : featureId;
}

/**
 * First, this function finds a single closest feature which is then used as an
 * anchor point for further calculations. Then, all other features that are less
 * than `radius` meters away from the anchor are returned.
 */
export function getClosestFeatures(
  centerPoint: GeoJSON.Position,
  features: MapFeature[],
  radius = 0
): MapFeature[] {
  const anchorFeature = getClosestFeature(centerPoint, features);
  if (isNil(anchorFeature)) return [];

  const closestFeatures: MapFeature[] = [];
  features.forEach((feature) => {
    if (
      feature.geometry.type === 'Point' &&
      anchorFeature.geometry.type === 'Point'
    ) {
      const dist = getLength([
        feature.geometry.coordinates,
        anchorFeature.geometry.coordinates,
      ]);

      if (dist <= radius) {
        closestFeatures.push(feature);
      }
    } else {
      // Non-point geometries are always included
      closestFeatures.push(feature);
    }
  });

  return closestFeatures;
}

function getClosestFeature(
  centerPoint: GeoJSON.Position,
  features: MapFeature[]
): MapFeature | undefined {
  let closestFeature: MapFeature | undefined;
  let closestDistance = Number.MAX_VALUE;

  features.forEach((feature) => {
    if (feature.geometry.type === 'Point') {
      const distance = getLength([centerPoint, feature.geometry.coordinates]);
      if (distance < closestDistance) {
        closestFeature = feature;
        closestDistance = distance;
      }
    } else {
      closestFeature = feature;
    }
  });

  return closestFeature;
}
